import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { HealthcareEntitiesService, IngestionService, ReportTypeEnum } from '../../../../../js/api';
import { api } from '../../../../../js/api/core/request';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { Button } from '../../../components/button';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '../../../components/dialog';
import { DropdownSelect, type CodeDefinitionOption } from '../../../components/dropdown-select';
import { FieldValidationErrorMessage } from '../../../components/field-validation-error-message';
import { Field, Fieldset, Label } from '../../../components/fieldset';
import { useCodeDefinitionsAsOptions } from '../../../hooks/useCodeDefinitions';

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const CSV_MIME = 'text/csv';

/** Browsers report inconsistent mime types for csv/xlsx (e.g. Windows sends
 * `application/vnd.ms-excel` for .csv); resolve from the extension so the
 * declared type always matches what the backend supports. */
const resolveMimeType = (file: File): string | null => {
  const name = file.name.toLowerCase();
  if (name.endsWith('.xlsx')) return XLSX_MIME;
  if (name.endsWith('.csv')) return CSV_MIME;
  return null;
};

const uploadFormSchema = z.object({
  healthcare_entity: z.string().min(1, 'Client is required'),
  report_type: z.string().min(1, 'Report type is required'),
  file: z.instanceof(File, { message: 'A file is required' }),
});

type UploadFormData = z.infer<typeof uploadFormSchema>;

type IngestUploadDialogProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

export function IngestUploadDialog({ open, onClose, onSuccess }: IngestUploadDialogProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const form = useForm<UploadFormData>({
    resolver: zodResolver(uploadFormSchema),
    mode: 'onChange',
    defaultValues: {
      healthcare_entity: '',
      report_type: '',
    },
  });

  const { data: reportTypeOptions, isLoading: isLoadingReportTypes } =
    useCodeDefinitionsAsOptions('IngestReportType');

  const { data: entitiesData, isLoading: isLoadingEntities } = useQuery({
    queryKey: ['healthcare-entities', 'ingest-upload'],
    queryFn: () => HealthcareEntitiesService.healthcareEntitiesList({ limit: 250, offset: 0 }),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const entityOptions: CodeDefinitionOption[] = useMemo(
    () =>
      (entitiesData?.results ?? [])
        .map((entity) => ({ value: entity.id, label: entity.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [entitiesData]
  );

  const resetForm = () => {
    form.reset({ healthcare_entity: '', report_type: '' });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const uploadMutation = useMutation({
    mutationFn: async (data: UploadFormData) => {
      const mimeType = resolveMimeType(data.file);
      if (!mimeType) {
        throw new Error('Only .xlsx and .csv files are supported.');
      }

      const uploadResponse = await IngestionService.ingestionFilesUploadRequestCreate({
        requestBody: {
          healthcare_entity: data.healthcare_entity,
          report_type: data.report_type as ReportTypeEnum,
          original_filename: data.file.name,
          file_size: data.file.size,
          mime_type: mimeType as never,
        },
      });

      const putResponse = await fetch(uploadResponse.upload_url, {
        method: 'PUT',
        body: data.file,
        headers: { 'Content-Type': mimeType },
      });
      if (!putResponse.ok) {
        throw new Error(`S3 upload failed with status ${putResponse.status}`);
      }

      return IngestionService.ingestionFilesConfirmCreate({
        id: uploadResponse.ingest_file_id,
      });
    },
    onSuccess: () => {
      toast.success('File uploaded — processing has started');
      resetForm();
      onSuccess();
      onClose();
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Upload failed' });
    },
  });

  const onSubmit = form.handleSubmit((data) => uploadMutation.mutate(data));

  const selectedEntity = form.watch('healthcare_entity');
  const selectedReportType = form.watch('report_type');

  // Column maps are per (client, report type) — used to decide whether the
  // template download reflects the client's headers or our standard format.
  const { data: columnMapsData } = useQuery({
    queryKey: ['ingest-column-maps', selectedEntity, selectedReportType],
    queryFn: () =>
      IngestionService.ingestionColumnMapsList({
        healthcareEntity: selectedEntity,
        reportType: selectedReportType as ReportTypeEnum,
      }),
    enabled: !!(selectedEntity && selectedReportType),
    staleTime: 5 * 60 * 1000,
  });
  const clientHasOverrides = (columnMapsData?.results?.length ?? 0) > 0;

  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const handleDownloadTemplate = async () => {
    if (!selectedReportType) return;
    setIsDownloadingTemplate(true);
    try {
      const reportType = selectedReportType;
      const params = new URLSearchParams({ report_type: reportType });
      if (selectedEntity) {
        params.set('healthcare_entity', selectedEntity);
      }
      const response = await api.get<Blob>(`/api/ingestion/files/template/?${params.toString()}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}_template.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      handleApiError(error as Error, { messagePrefix: 'Template download failed' });
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} size="xl">
      <DialogTitle className="tw:!text-2xl tw:!text-brand-green-0 tw:!mt-0 tw:!pt-0">
        Upload Report File
      </DialogTitle>
      <DialogBody>
        <form onSubmit={onSubmit}>
          <Fieldset>
            <div className="tw:grid tw:grid-cols-1 tw:gap-4">
              <Field>
                <Label>
                  Report Type <span className="tw:text-stoplight-danger">*</span>
                </Label>
                <DropdownSelect
                  allOptions={reportTypeOptions ?? []}
                  value={selectedReportType || null}
                  onChange={(option) => {
                    const next = Array.isArray(option) ? (option[0] ?? null) : option;
                    form.setValue('report_type', next ? next.value : '', {
                      shouldValidate: true,
                    });
                  }}
                  isMulti={false}
                  placeholder={isLoadingReportTypes ? 'Loading types...' : 'Select a report type'}
                  isDisabled={isLoadingReportTypes}
                />
                {form.formState.errors.report_type && (
                  <FieldValidationErrorMessage
                    message={form.formState.errors.report_type.message}
                  />
                )}
              </Field>
              <Field>
                <Label>
                  Client <span className="tw:text-stoplight-danger">*</span>
                </Label>
                <DropdownSelect
                  allOptions={entityOptions}
                  value={selectedEntity || null}
                  onChange={(option) => {
                    const next = Array.isArray(option) ? (option[0] ?? null) : option;
                    form.setValue('healthcare_entity', next ? next.value : '', {
                      shouldValidate: true,
                    });
                  }}
                  isMulti={false}
                  placeholder={isLoadingEntities ? 'Loading clients...' : 'Select a client'}
                  isDisabled={isLoadingEntities}
                />
                {form.formState.errors.healthcare_entity && (
                  <FieldValidationErrorMessage
                    message={form.formState.errors.healthcare_entity.message}
                  />
                )}
              </Field>
              <Field>
                <Label>
                  File <span className="tw:text-stoplight-danger">*</span>
                </Label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.csv"
                  disabled={uploadMutation.isPending}
                  data-testid="ingest-upload-file-input"
                  onChange={(e) => {
                    const next = e.target.files?.[0];
                    form.setValue('file', (next ?? undefined) as unknown as File, {
                      shouldValidate: true,
                    });
                  }}
                  className="tw:block tw:w-full tw:text-sm tw:text-zinc-700 tw:file:mr-3 tw:file:rounded-md tw:file:border-0 tw:file:bg-brand-green-2 tw:file:px-3 tw:file:py-1.5 tw:file:text-sm tw:file:font-medium tw:file:text-brand-green-0 tw:hover:file:bg-brand-green-1 tw:hover:file:text-white tw:disabled:opacity-50 tw:disabled:cursor-not-allowed"
                />
                {form.formState.errors.file && (
                  <FieldValidationErrorMessage message={form.formState.errors.file.message} />
                )}
                <p className="tw:!mt-1 tw:text-xs tw:text-zinc-500">
                  Excel (.xlsx) or CSV (.csv). The file is stored as received, then processed.
                </p>
              </Field>
            </div>
          </Fieldset>
        </form>
      </DialogBody>
      <DialogActions className="tw:sm:justify-between">
        <Button
          color="brand-green-outline"
          onClick={handleDownloadTemplate}
          disabled={!selectedReportType || isDownloadingTemplate}
          title="CSV of the required file headers — the selected client's column names when they have overrides, otherwise our standard format. Select a report type first."
          data-testid="ingest-template-download"
        >
          <i className="far fa-fw fa-download" aria-hidden="true" />
          {isDownloadingTemplate
            ? 'Preparing...'
            : selectedEntity && clientHasOverrides
              ? "Client's Template"
              : 'Standard Template'}
        </Button>
        <div className="tw:flex tw:w-full tw:flex-col-reverse tw:items-center tw:gap-3 tw:*:w-full tw:sm:w-auto tw:sm:flex-row tw:sm:*:w-auto">
          <Button color="light" onClick={handleClose} disabled={uploadMutation.isPending}>
            Cancel
          </Button>
          <Button
            color="brandgreen0"
            onClick={onSubmit}
            disabled={!form.formState.isValid || uploadMutation.isPending}
            data-testid="ingest-upload-submit"
          >
            {uploadMutation.isPending ? 'Uploading...' : 'Upload'}
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
}
